import { TableData } from '@/src/types/flightOffer'
import { Check, ContentCopy } from '@mui/icons-material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import {
    Box,
    Button,
    Card,
    CardMedia,
    Container,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'

interface OfferDetailProps {
    isLoading: boolean
    isError: boolean
    offer?: any
    desktopImageUrl?: string
    mobileImageUrl?: string
    copied?: boolean
    processedTables?: TableData[]
    t: (key: string) => string
    formatDate: (date: string, format?: string) => string
    onCopyCode?: () => void
    errorComponent?: React.ReactNode
    locale?: string
    isAppDeals?: boolean
    isCT?: boolean
}

const OfferDetail: React.FC<OfferDetailProps> = ({
    isLoading,
    isError,
    offer,
    desktopImageUrl,
    mobileImageUrl,
    copied,
    processedTables,
    t,
    formatDate,
    onCopyCode,
    errorComponent,
    locale,
    isAppDeals,
    isCT
}) => {
    const renderTable = (table: TableData) => {
        if (!table.hasData) return null

        const headers = table.data[0]
        const rows = table.data.slice(1)

        return (
            <Box key={table.tableHeader} sx={{ mb: { xs: 3, md: 4 } }}>
                {table.tableHeader && (
                    <Typography
                        variant="h6"
                        sx={{
                            mb: { xs: 1.5, md: 2 },
                            px: { xs: 1, sm: 0 },
                            fontWeight: 600,
                            fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
                            textDecoration: 'underline',
                            textUnderlineOffset: '4px'
                        }}
                    >
                        {table.tableHeader}
                    </Typography>
                )}
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: { xs: 0, sm: 1 }
                    }}
                >
                    <Table
                        sx={{
                            width: '100%',
                            tableLayout: 'fixed'
                        }}
                    >
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                {headers.map((header, index) => (
                                    <TableCell
                                        key={index}
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                                            py: { xs: 0.75, sm: 1, md: 1.5 },
                                            px: { xs: 0.5, sm: 1, md: 2 },
                                            borderRight: index < headers.length - 1 ? '1px solid' : 'none',
                                            borderColor: 'divider',
                                            whiteSpace: 'normal',
                                            wordWrap: 'break-word',
                                            lineHeight: { xs: 1.2, sm: 1.4 }
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {row.map((cell, cellIndex) => (
                                        <TableCell
                                            key={cellIndex}
                                            align="center"
                                            sx={{
                                                fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                                                py: { xs: 0.5, sm: 0.75, md: 1 },
                                                px: { xs: 0.5, sm: 1, md: 2 },
                                                borderRight: cellIndex < row.length - 1 ? '1px solid' : 'none',
                                                borderColor: 'divider',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word',
                                                lineHeight: { xs: 1.2, sm: 1.4 },
                                                overflowWrap: 'break-word'
                                            }}
                                        >
                                            {cell || '-'}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        )
    }

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Skeleton variant="rectangular" height={60} sx={{ mb: 3 }} />
                <Skeleton variant="rectangular" height={400} sx={{ mb: 3 }} />
                <Skeleton variant="text" sx={{ fontSize: '2rem' }} />
                <Skeleton variant="text" sx={{ fontSize: '1.2rem' }} />
                <Skeleton variant="text" width="60%" />
            </Container>
        )
    }

    if (isError) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1, display: 'flex' }}>{errorComponent}</Box>
            </Container>
        )
    }

    return (
        <Box sx={{ bgcolor: 'grey.50' }}>
            <Container
                maxWidth="xl"
                sx={{
                    mt: { xs: 0, sm: 0, md: 3 },
                    mb: { xs: 0, sm: 3, md: 4 },
                    px: { xs: 0, sm: 0, md: 0 }
                }}
            >
                {/* Main Content Card */}
                <Card
                    sx={{
                        overflow: 'hidden',
                        boxShadow: 'none',
                        borderRadius: { xs: 0, sm: 0, md: 2 },
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    {/* Hero Image - Only show if images are available and working */}
                    {(mobileImageUrl || desktopImageUrl) && (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                overflow: 'hidden',
                                bgcolor: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {/* Mobile Image - visible on xs and sm screens */}
                            {mobileImageUrl && (
                                <CardMedia
                                    component="img"
                                    image={mobileImageUrl}
                                    alt={offer?.title}
                                    sx={{
                                        width: '100%',
                                        height: 'auto',
                                        maxHeight: { xs: '350px', sm: '450px', md: '608px' },
                                        display: { xs: 'block', md: 'none' },
                                        objectFit: 'contain',
                                        bgcolor: 'white'
                                    }}
                                    onError={e => {
                                        // Hide the image if it fails to load
                                        e.currentTarget.style.display = 'none'
                                    }}
                                />
                            )}
                            {/* Desktop Image - visible on md and larger screens */}
                            {desktopImageUrl && (
                                <CardMedia
                                    component="img"
                                    image={desktopImageUrl}
                                    alt={offer?.title}
                                    sx={{
                                        width: '100%',
                                        height: 'auto',
                                        maxHeight: { xs: '350px', sm: '450px', md: '608px' },
                                        display: { xs: 'none', md: 'block' },
                                        objectFit: 'contain',
                                        bgcolor: 'white'
                                    }}
                                    onError={e => {
                                        // Hide the image if it fails to load
                                        e.currentTarget.style.display = 'none'
                                    }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Content Section */}
                    <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3.5 } }}>
                        {/* Title and Expiry Date Section */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                justifyContent: 'space-between',
                                flexDirection: { xs: 'column-reverse', sm: 'column-reverse' },
                                mb: 3,
                                gap: { xs: 0.5, sm: 1 }
                            }}
                        >
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    color: 'textColors.primary',
                                    fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2rem' },
                                    lineHeight: { xs: 1.2, sm: 1.3 }
                                }}
                            >
                                {offer?.heading || offer?.title}
                            </Typography>
                            {offer?.expiryDate && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        color: 'text.secondary',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        fontWeight: 500,
                                        flexShrink: 0,
                                        alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                                        mb: { xs: 0.5, sm: 0 }
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: 'inherit',
                                            color: 'black'
                                        }}
                                    >
                                        {t('expires')}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: 'inherit',
                                            fontWeight: 600,
                                            color: 'text.disabled'
                                        }}
                                    >
                                        {formatDate(offer.expiryDate, 'MMM D, YYYY')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

                        {/* Description Paragraph */}
                        {offer?.paragraph && (
                            <Box
                                sx={{
                                    mb: { xs: 3, sm: 4 }
                                }}
                            >
                                <Box
                                    component="div"
                                    dangerouslySetInnerHTML={{ __html: offer.paragraph }}
                                    sx={{
                                        lineHeight: { xs: 1.5, sm: 1.6, md: 1.7 },
                                        color: 'textColors.primary',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                        '& a': {
                                            color: 'primary.main',
                                            textDecoration: 'underline',
                                            '&:hover': {
                                                textDecoration: 'none'
                                            }
                                        },
                                        '& strong, & b': {
                                            fontWeight: 700
                                        },
                                        '& em, & i': {
                                            fontStyle: 'italic'
                                        },
                                        '& br': {
                                            display: 'block',
                                            content: '""',
                                            marginTop: '0.5em'
                                        }
                                    }}
                                />
                            </Box>
                        )}

                        {/* Key Details */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: offer?.couponCode ? 'repeat(2, 1fr)' : '1fr',
                                    md: offer?.couponCode ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)'
                                },
                                gap: { xs: 1.5, sm: 2 },
                                mb: { xs: 3, sm: 4 }
                            }}
                        >
                            {offer?.couponCode && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 1.5, sm: 2, md: 2.5 },
                                        border: '2px dashed',
                                        borderColor: 'primary.main',
                                        bgcolor: 'primary.50',
                                        textAlign: 'center',
                                        position: 'relative'
                                    }}
                                >
                                    <LocalOfferIcon
                                        sx={{
                                            color: 'primary.main',
                                            mb: { xs: 0.5, sm: 1 },
                                            fontSize: { xs: 18, sm: 20, md: 24 }
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{
                                            fontWeight: 500,
                                            mb: 0.5,
                                            fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' }
                                        }}
                                    >
                                        {t('promoCode')}
                                    </Typography>
                                    <Box
                                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontWeight: 'bold',
                                                color: 'primary.main',
                                                letterSpacing: 1,
                                                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
                                            }}
                                        >
                                            {offer.couponCode}
                                        </Typography>
                                        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                                            <IconButton
                                                size="small"
                                                onClick={onCopyCode}
                                                sx={{
                                                    p: { xs: 0.4, sm: 0.5 },
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        bgcolor: 'primary.100'
                                                    }
                                                }}
                                            >
                                                {copied ? (
                                                    <Check sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }} />
                                                ) : (
                                                    <ContentCopy sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }} />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Paper>
                            )}

                            {offer?.bookingPeriod && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 1.5, sm: 2, md: 2.5 },
                                        textAlign: 'center',
                                        bgcolor: 'grey.50',
                                        border: '1px solid',
                                        borderColor: 'divider'
                                    }}
                                >
                                    <CalendarTodayIcon
                                        sx={{
                                            color: 'text.secondary',
                                            mb: { xs: 0.5, sm: 1 },
                                            fontSize: { xs: 18, sm: 20, md: 24 }
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{
                                            fontWeight: 500,
                                            mb: 0.5,
                                            fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' }
                                        }}
                                    >
                                        {t('bookingPeriod')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color="text.primary"
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' } }}
                                    >
                                        {offer.bookingPeriod}
                                    </Typography>
                                </Paper>
                            )}

                            {offer?.travelPeriod && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 1.5, sm: 2, md: 2.5 },
                                        textAlign: 'center',
                                        bgcolor: 'grey.50',
                                        border: '1px solid',
                                        borderColor: 'divider'
                                    }}
                                >
                                    <FlightTakeoffIcon
                                        sx={{
                                            color: 'text.secondary',
                                            mb: { xs: 0.5, sm: 1 },
                                            fontSize: { xs: 18, sm: 20, md: 24 }
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{
                                            fontWeight: 500,
                                            mb: 0.5,
                                            fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' }
                                        }}
                                    >
                                        {t('travelPeriod')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color="text.primary"
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' } }}
                                    >
                                        {offer.travelPeriod}
                                    </Typography>
                                </Paper>
                            )}
                        </Box>

                        {/* Tables Section */}
                        {processedTables && processedTables.length > 0 && (
                            <Box
                                sx={{
                                    mb: 4,
                                    mx: { xs: -1, sm: 0 }
                                }}
                            >
                                {processedTables.map((table, index) => (
                                    <Box key={index}>{renderTable(table)}</Box>
                                ))}
                            </Box>
                        )}

                        {/* Book Now Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <Button
                                variant="customVariantPrimary"
                                size="large"
                                href={(() => {
                                    const queryParams = new URLSearchParams()

                                    if (isAppDeals) {
                                        queryParams.set('redirect', 'flights')
                                    }

                                    const queryString = queryParams.toString()
                                    const basePath = `/${locale || 'en'}`

                                    return queryString ? `${basePath}?${queryString}` : basePath
                                })()}
                                color="secondary"
                                sx={{
                                    px: { xs: 4, sm: 5, md: 6 },
                                    py: { xs: 1, sm: 1.25, md: 1.5 },
                                    borderRadius: { xs: 1.5, sm: 2 },
                                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' },
                                    maxWidth: { xs: '100%', sm: '280px' }
                                }}
                            >
                                {t('bookNow')}
                            </Button>
                        </Box>

                        <Divider sx={{ my: { xs: 3, sm: 4 } }} />

                        {/* Terms and Conditions */}
                        {offer?.termsAndConditions && offer.termsAndConditions.length > 0 && (
                            <Box>
                                <Typography
                                    variant="h5"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 600,
                                        mb: { xs: 2, sm: 3 },
                                        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.5rem' }
                                    }}
                                >
                                    {t('termsAndConditions')}
                                </Typography>
                                <List
                                    sx={{
                                        bgcolor: 'grey.50',
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                        p: { xs: 1.5, sm: 2 },
                                        px: { xs: 1, sm: 2 }
                                    }}
                                >
                                    {offer.termsAndConditions.map((term: string, index: number) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                display: 'list-item',
                                                listStyleType: 'disc',
                                                ml: { xs: 1.5, sm: 2 },
                                                pl: 0,
                                                py: { xs: 0.4, sm: 0.5 }
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                                                            lineHeight: { xs: 1.4, sm: 1.5 }
                                                        }}
                                                    >
                                                        {term}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                </Card>
            </Container>
        </Box>
    )
}

export { OfferDetail }
export type { OfferDetailProps }
